import React, { useMemo, useEffect, useState } from "react";
import { useAuth } from "@via/hooks/useAuth";
import AccountInfoForm from "@via/pages/authentication/self-service-signup/progress/account-info-form.tsx";
import { StepProgress } from "@via/components/step-progress.tsx";
import FoundryConnectLogo from "@via/components/foundry-connect-logo.tsx";
import { OnboardingStep } from "common";
import GuestConfirmationScreen from "@via/pages/authentication/guest-signup/progress/guest-confirmation-screen.tsx";

const guestStepComponents = [AccountInfoForm, GuestConfirmationScreen];

const guestStepNames = ["Account Info", "Confirmation"];

interface GuestSignupProgressProps {
  email: string;
}

const GuestSignupProgress: React.FC<GuestSignupProgressProps> = ({ email }) => {
  const { user } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const steps = useMemo(() => {
    return guestStepNames.map((name, index) => ({
      name,
      isCompleted: index < currentStepIndex,
    }));
  }, [currentStepIndex]);

  const handleNextStep = () => {
    if (currentStepIndex < guestStepComponents.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      console.log("Final step completed! Submitting form...");
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex === 0) return;
    if (steps[stepIndex].isCompleted) {
      setCurrentStepIndex(stepIndex);
    }
  };

  // Set initial step based on user's onboarding status
  useEffect(() => {
    if (!user?.onboardingStep) return;

    if (user.onboardingStep === OnboardingStep.AccountCreated) {
      setCurrentStepIndex(1);
    }
  }, [user?.onboardingStep]);

  const CurrentStepComponent = guestStepComponents[currentStepIndex];

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8">
      <FoundryConnectLogo />
      <div className="flex flex-col items-center justify-center w-full max-w-3xl">
        <StepProgress
          steps={steps}
          currentStepIndex={currentStepIndex}
          onStepClick={handleStepClick}
        />
        <div className="mt-10">
          <CurrentStepComponent onNextStep={handleNextStep} email={email} />
        </div>
      </div>
    </div>
  );
};

export default GuestSignupProgress;
